import { useTenantNodeContext } from 'contexts/TenantNodeContext';
import { useEffect, useState } from 'react';

import {
	getAccessTokenPotentiallyRenewingIt,
	getTenantNodeIds,
	getTokensFromLocalStorage,
} from 'functions/tokenFunctions';

export interface TenantNode {
	id: string;
	name: string;
	parentId: string | null;
	metadata?: Record<string, any>;
	children: TenantNode[];
}

export function useTenantNodes() {
	const { fullTree, setFullTree, getAccessibleNodes, isLoading, setIsLoading } =
		useTenantNodeContext();
	const [error, setError] = useState<string | null>(null);

	// Helper function to parse JWT
	const parseJwt = (token: string) => {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split('')
				.map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join('')
		);
		return JSON.parse(jsonPayload);
	};

	useEffect(() => {
		const fetchTenantTree = async () => {
			// If we already have the full tree, don't fetch again
			if (fullTree) {
				return;
			}

			// Get Tenant.Id from JWT for the root tree
			const tokens = getTokensFromLocalStorage();
			if (!tokens.hasValue()) {
				setError('Authentication required');
				return;
			}

			const tokenData = tokens.get();
			const storedAccessToken = tokenData.accessToken;
			if (!storedAccessToken) {
				setError('Access token not found');
				return;
			}
			const jwtPayload = parseJwt(storedAccessToken.token);
			if (!jwtPayload['Tenant.Id']) {
				setError('Tenant ID not found in token');
				return;
			}

			// Get access token for authentication
			const accessToken = await getAccessTokenPotentiallyRenewingIt();
			if (!accessToken.hasValue()) {
				setError('Authentication required');
				return;
			}

			setIsLoading(true);
			setError(null);

			try {
				// Fetch the full tree using Tenant.Id from JWT
				const accessTokenData = accessToken.get();
				const response = await fetch(
					`/api/user/TenantNode/tree/${jwtPayload['Tenant.Id']}`,
					{
						headers: {
							Authorization: `Bearer ${accessTokenData.token}`,
							'Content-Type': 'application/json',
						},
					}
				);

				if (response.ok) {
					const treeData = await response.json();

					// Convert the API response to our TenantNode structure
					const convertedNodes = convertApiResponseToTenantNodes(treeData);
					setFullTree(convertedNodes);
				} else {
					setError(
						`Failed to fetch tenant tree: ${response.status} ${response.statusText}`
					);
				}
			} catch (err) {
				setError('Failed to fetch tenant tree');
			} finally {
				setIsLoading(false);
			}
		};

		fetchTenantTree();
	}, [fullTree, setFullTree, setIsLoading]);

	// Get accessible nodes based on Tenant.NodeIds from JWT
	const getAccessibleNodesForUser = () => {
		const tenantNodeIds = getTenantNodeIds();

		if (!tenantNodeIds || tenantNodeIds.length === 0 || !fullTree) {
			return [];
		}

		const accessibleNodes = getAccessibleNodes(tenantNodeIds);
		return accessibleNodes;
	};

	return {
		nodes: getAccessibleNodesForUser(),
		isLoading,
		error,
		fullTree,
	};
}

// Helper function to convert API response to TenantNode structure
function convertApiResponseToTenantNodes(treeData: any): TenantNode[] {
	// The API returns TenantNodeTreeDto with Nodes array
	// We need to convert each node to our TenantNode interface
	const convertNode = (node: any): TenantNode => ({
		id: node.id,
		name: node.name,
		parentId: node.parentId,
		metadata: node.metadata,
		children: node.children?.map(convertNode) || [],
	});

	return treeData.nodes?.map(convertNode) || [];
}
